import React from "react";
import { alpha } from "@mui/material/styles";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { Tab, Visibility, VisibilityOff } from "@mui/icons-material";
import { msSinceIso, formatTeamPresenceAge } from "../utils/teamPresenceFormatting";

/** Diferença em ms abaixo da qual tratamos “tempo na tela” e “última interação” como iguais (evita texto duplicado). */
const SCREEN_IDLE_MERGE_MS = 2500;

/**
 * Chips de abas (legado / Xpro 4) e lista de telas por aba no Xpro 4.
 * @param {object} props
 * @param {boolean} props.show — ex.: usuário online com abas
 * @param {number} props.socketTabs
 * @param {{ legacyTabCount: number, xpro4Tabs: object[] }|null|undefined} props.presenceDetail
 * @param {(key: string, opts?: object) => string} props.tLive — settings
 * @param {(path: string) => string} props.screenTitleForPath
 * @param {number} props.livePresenceNowMs
 * @param {"default"|"dense"} [props.density]
 */
export default function TeamPresenceTabBreakdown({
  show,
  socketTabs,
  presenceDetail,
  tLive,
  screenTitleForPath,
  livePresenceNowMs,
  density = "default",
}) {
  const det = presenceDetail;
  const chipH = density === "dense" ? 26 : 28;
  const tabIcon = density === "dense" ? 12 : 14;

  if (!show || socketTabs <= 0) return null;

  const tabRows = det?.xpro4Tabs?.length > 0;
  const listMaxHeight = density === "dense" ? 112 : 128;

  return (
    <Stack spacing={density === "dense" ? 0.75 : 1}>
      <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 0.75, rowGap: 0.5, alignItems: "center" }}>
        {det && (det.legacyTabCount > 0 || det.xpro4Tabs.length > 0) ? (
          <>
            {det.legacyTabCount > 0 ? (
              <Tooltip title={tLive("dashboardPage.live.teamTabsLegacyTooltip")}>
                <Chip
                  size="small"
                  icon={<Tab sx={{ "&&": { fontSize: tabIcon } }} />}
                  label={tLive("dashboardPage.live.teamTabsLegacyBadge", { count: det.legacyTabCount })}
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    maxWidth: "100%",
                    height: chipH,
                    bgcolor: (th) => alpha(th.palette.warning.dark, th.palette.mode === "dark" ? 0.4 : 0.18),
                    color: "text.primary",
                    "& .MuiChip-icon": { color: "inherit" },
                    "& .MuiChip-label": { py: 0 },
                  }}
                />
              </Tooltip>
            ) : null}
            {det.xpro4Tabs.length > 0 ? (
              <Tooltip title={tLive("dashboardPage.live.teamTabsXpro4Tooltip")}>
                <Chip
                  size="small"
                  icon={<Tab sx={{ "&&": { fontSize: tabIcon } }} />}
                  label={tLive("dashboardPage.live.teamTabsXpro4Badge", { count: det.xpro4Tabs.length })}
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    maxWidth: "100%",
                    height: chipH,
                    bgcolor: (th) => alpha(th.palette.primary.dark, th.palette.mode === "dark" ? 0.45 : 0.14),
                    color: "text.primary",
                    "& .MuiChip-icon": { color: "inherit" },
                    "& .MuiChip-label": { py: 0 },
                  }}
                />
              </Tooltip>
            ) : null}
          </>
        ) : (
          <Chip
            size="small"
            icon={<Tab sx={{ "&&": { fontSize: tabIcon } }} />}
            label={tLive("dashboardPage.live.openTabsCount", { count: socketTabs })}
            variant="filled"
            sx={{
              fontWeight: 600,
              maxWidth: "100%",
              height: chipH,
              bgcolor: (th) => alpha(th.palette.success.dark, th.palette.mode === "dark" ? 0.35 : 0.12),
              color: "text.primary",
              "& .MuiChip-icon": { color: "inherit" },
              "& .MuiChip-label": { py: 0 },
            }}
          />
        )}
      </Stack>

      {det && det.xpro4Tabs.length > 0 ? (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.375, fontWeight: 600, lineHeight: 1.35, letterSpacing: 0.02 }}
          >
            {tLive("dashboardPage.live.teamXpro4TabsHeading")}
          </Typography>
          <Box
            sx={{
              maxHeight: det.xpro4Tabs.length > 2 ? listMaxHeight : "none",
              overflowY: det.xpro4Tabs.length > 2 ? "auto" : "visible",
              pr: det.xpro4Tabs.length > 2 ? 0.25 : 0,
              scrollbarWidth: "thin",
            }}
          >
            <Stack spacing={density === "dense" ? 0.5 : 0.625}>
              {det.xpro4Tabs.map((tab) => {
                const title = screenTitleForPath(tab.path);
                const onScreenMs = msSinceIso(tab.since, livePresenceNowMs);
                const idleMs = msSinceIso(tab.lastActivityAt, livePresenceNowMs);
                const durScreen = formatTeamPresenceAge(onScreenMs, tLive);
                const durIdle = formatTeamPresenceAge(idleMs, tLive);
                const screenOk = onScreenMs != null && Number.isFinite(onScreenMs);
                const idleOk = idleMs != null && Number.isFinite(idleMs);
                const mergedTimes =
                  screenOk && idleOk && Math.abs(onScreenMs - idleMs) <= SCREEN_IDLE_MERGE_MS;

                let timeLine;
                if (mergedTimes) {
                  timeLine = tLive("dashboardPage.live.teamPresenceScreenIdleMerged", { duration: durScreen });
                } else if (screenOk && idleOk) {
                  timeLine = tLive("dashboardPage.live.teamPresenceScreenIdleDual", {
                    durationScreen: durScreen,
                    durationIdle: durIdle,
                  });
                } else if (screenOk) {
                  timeLine = tLive("dashboardPage.live.teamPresenceOnScreen", { duration: durScreen });
                } else if (idleOk) {
                  timeLine = tLive("dashboardPage.live.teamPresenceIdle", { duration: durIdle });
                } else {
                  timeLine = "—";
                }

                const focusTip = tab.focused
                  ? tLive("dashboardPage.live.teamPresenceFocusYes")
                  : tLive("dashboardPage.live.teamPresenceFocusNo");

                return (
                  <Box
                    key={tab.tabKey}
                    sx={{
                      borderRadius: 1,
                      px: 1,
                      py: density === "dense" ? 0.375 : 0.5,
                      minWidth: 0,
                      bgcolor: (th) => alpha(th.palette.text.primary, th.palette.mode === "dark" ? 0.06 : 0.04),
                      border: 1,
                      borderColor: (th) => alpha(th.palette.divider, 0.85),
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.75} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: density === "dense" ? "0.75rem" : "0.8125rem",
                          lineHeight: 1.35,
                          minWidth: 0,
                          flex: 1,
                        }}
                        noWrap
                        title={tab.path || title || undefined}
                      >
                        {title}
                      </Typography>
                      <Tooltip title={focusTip}>
                        <Box
                          component="span"
                          aria-label={focusTip}
                          sx={{
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            color: tab.focused ? "success.main" : "text.secondary",
                            bgcolor: (th) =>
                              alpha(
                                tab.focused ? th.palette.success.main : th.palette.action.active,
                                tab.focused ? (th.palette.mode === "dark" ? 0.22 : 0.12) : 0.08
                              ),
                          }}
                        >
                          {tab.focused ? (
                            <Visibility sx={{ fontSize: density === "dense" ? 16 : 17 }} />
                          ) : (
                            <VisibilityOff sx={{ fontSize: density === "dense" ? 16 : 17, opacity: 0.85 }} />
                          )}
                        </Box>
                      </Tooltip>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        lineHeight: 1.4,
                        fontSize: density === "dense" ? "0.68rem" : "0.7rem",
                      }}
                    >
                      {timeLine}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}
